export type AdminRole = 
  | 'SuperAdmin'
  | 'PlatformAdmin'
  | 'SecurityAnalyst'
  | 'IncidentCommander'
  | 'SupportAgent'
  | 'DataSteward';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  mfaEnforced: boolean;
  status: 'ACTIVE' | 'SUSPENDED';
  lastLogin: string;
}

export interface SystemIncident {
  id: string;
  title: string;
  domain: 'PRODUCT' | 'DATA' | 'SECURITY' | 'AI' | 'INFRASTRUCTURE' | 'REVENUE';
  severity: 'SEV0' | 'SEV1' | 'SEV2' | 'SEV3';
  status: 'DETECTED' | 'INVESTIGATING' | 'MITIGATING' | 'MONITORING' | 'RESOLVED';
  commander: string;
  startedAt: string;
  affectedMarkets: string[];
}

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  status: 'OFF' | 'CANARY' | 'PARTIAL' | 'ON';
  owner: string;
  environment: 'PRODUCTION' | 'STAGING';
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  actor: string;
  role: AdminRole;
  action: string;
  resource: string;
  status: 'SUCCESS' | 'DENIED' | 'PENDING_APPROVAL';
  environment: string;
}

export const MOCK_ADMIN_USERS: AdminUser[] = [
  {
    id: 'usr-admin-1',
    email: 'admin.super@yamoopay.com',
    name: 'Chief SuperAdmin',
    role: 'SuperAdmin',
    mfaEnforced: true,
    status: 'ACTIVE',
    lastLogin: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
  },
  {
    id: 'usr-admin-2',
    email: 'security.lead@yamoopay.com',
    name: 'Sarah Connor',
    role: 'SecurityAnalyst',
    mfaEnforced: true,
    status: 'ACTIVE',
    lastLogin: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
  },
  {
    id: 'usr-admin-3',
    email: 'sre.commander@yamoopay.com',
    name: 'John SRE',
    role: 'IncidentCommander',
    mfaEnforced: true,
    status: 'ACTIVE',
    lastLogin: new Date(Date.now() - 1000 * 60 * 240).toISOString(),
  },
];

export const MOCK_INCIDENTS: SystemIncident[] = [
  {
    id: 'INC-2026-001',
    title: 'FX Rate Provider Latency Spike (ECB Primary)',
    domain: 'DATA',
    severity: 'SEV2',
    status: 'MONITORING',
    commander: 'John SRE',
    startedAt: new Date(Date.now() - 1000 * 60 * 60 * 2.5).toISOString(),
    affectedMarkets: ['Eurozone', 'Global'],
  },
  {
    id: 'INC-2026-002',
    title: 'AI Tool Timeout on Complex Multi-Intent Query',
    domain: 'AI',
    severity: 'SEV3',
    status: 'INVESTIGATING',
    commander: 'Chief AI Officer',
    startedAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    affectedMarkets: ['US', 'Global'],
  },
];

export const MOCK_FEATURE_FLAGS: FeatureFlag[] = [
  {
    key: 'enable_ai_search_assistant',
    name: 'Ask YamooPay AI Assistant',
    description: 'Enables the natural language orchestration layer and tool calling.',
    status: 'ON',
    owner: 'AI Platform Team',
    environment: 'PRODUCTION',
  },
  {
    key: 'enable_realtime_fx_ws',
    name: 'Real-Time FX WebSockets',
    description: 'Pushes live mid-market exchange rate updates.',
    status: 'CANARY',
    owner: 'Currency Engineering',
    environment: 'PRODUCTION',
  },
  {
    key: 'enable_mortgage_ai_copilot',
    name: 'Mortgage AI Copilot',
    description: 'Provides contextual AI guidance on mortgage refinancing scenarios.',
    status: 'OFF',
    owner: 'Wealth Team',
    environment: 'PRODUCTION',
  },
];

export const MOCK_AUDIT_LOGS: AuditLogEntry[] = [
  {
    id: 'aud-901',
    timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    actor: 'admin.super@yamoopay.com',
    role: 'SuperAdmin',
    action: 'UPDATE_CONFIG',
    resource: 'ai.model.default',
    status: 'SUCCESS',
    environment: 'PRODUCTION',
  },
  {
    id: 'aud-902',
    timestamp: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
    actor: 'security.lead@yamoopay.com',
    role: 'SecurityAnalyst',
    action: 'FORCE_LOGOUT_USER',
    resource: 'usr-compromised-99',
    status: 'SUCCESS',
    environment: 'PRODUCTION',
  },
  {
    id: 'aud-903',
    timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    actor: 'sre.commander@yamoopay.com',
    role: 'IncidentCommander',
    action: 'TRIGGER_KILL_SWITCH',
    resource: 'provider.fx.ecb_backup',
    status: 'SUCCESS',
    environment: 'PRODUCTION',
  },
];
